import { useCallback, useEffect, useRef, useState } from "react";
import { useRefreshOnReconnect } from "@/hooks/use-backend-connectivity";
import { fetchWithAuth } from "@/lib/utils";
import { cacheScope } from "@/lib/ui-cache";

export type HierarchyCapability = "loading" | "supported" | "unsupported" | "error";
const cache = new Map<string, { state: HierarchyCapability; expires: number }>();
const TTL_MS = 30_000;

export async function probeTaskHierarchyCapability(
  projectId: string,
  getToken: () => Promise<string>,
  fetcher: typeof fetchWithAuth = fetchWithAuth,
): Promise<"supported" | "unsupported" | "error"> {
  try {
    const response = await fetcher(`/api/projects/${encodeURIComponent(projectId)}/task-hierarchy/capabilities`, {}, getToken);
    if ([404, 405, 422].includes(response.status)) return "unsupported";
    if (!response.ok) return "error";
    const data: { supported?: boolean } = await response.json();
    return data.supported === true ? "supported" : "error";
  } catch {
    return "error";
  }
}

/** A failed probe can be retried; only explicit compatibility responses disable the feature. */
export function useTaskHierarchyCapability(projectId: string | null, getToken: () => Promise<string>) {
  const [status, setStatus] = useState<{ projectId: string | null; value: HierarchyCapability }>({ projectId: null, value: "loading" });
  const state = status.projectId === projectId ? status.value : "loading";
  const currentProjectRef = useRef(projectId);
  currentProjectRef.current = projectId;
  const retry = useCallback(async (force = false) => {
    if (!projectId) return;
    const key = `${cacheScope()}:${projectId}`;
    const cached = cache.get(key);
    if (!force && cached && cached.expires > Date.now()) {
      if (currentProjectRef.current === projectId) setStatus({ projectId, value: cached.state });
      return;
    }
    setStatus({ projectId, value: "loading" });
    const value = await probeTaskHierarchyCapability(projectId, getToken);
    if (value !== "error") cache.set(key, { state: value, expires: Date.now() + TTL_MS });
    if (currentProjectRef.current === projectId) setStatus({ projectId, value });
  }, [getToken, projectId]);
  useEffect(() => { void retry(); }, [retry]);
  useRefreshOnReconnect(() => { if (projectId) cache.delete(`${cacheScope()}:${projectId}`); void retry(true); }, Boolean(projectId));
  return { state, retry: () => retry(true) };
}
